import { create } from 'zustand';
import {
  EventBus, useGameStore, TYPING_TTL_MS, ATTACHMENTS_PER_MESSAGE, ATTACHMENT_UPLOAD_MAX_BYTES, REPLY_PREVIEW_LENGTH,
  type AttachmentDTO, type ConversationDTO, type GroupInfo, type MessageDTO, type MessageReactionDTO, type MessagingEvent, type ReplyPreviewDTO,
} from '@campus/core';
import { API_BASE } from '../lib/apiBase.js';
import { recordReactionUse } from '../lib/reactionUsage.js';
import { sessionInfo, useMembersStore } from '../ui/people/membersStore.js';
import { mentionsName } from './mentions.js';
import { MessagingApi, MessagingApiError, MessagingSocket, type MessagingAuth, type SocketStatus } from './client.js';
import { previewText } from '../ui/messaging/format.js';

/**
 * État de la messagerie côté campus web (tasks/messagerie-plan.md) :
 * conversations, fils chargés, « en train d'écrire », connexion temps réel.
 * La base fait foi : à chaque (re)connexion de la socket, la liste et les fils
 * ouverts sont relus (messages manqués, modifiés, supprimés).
 */

/** Message affiché : ceux en cours d'envoi ou en échec n'ont pas encore d'id serveur. */
export interface ThreadMessage extends MessageDTO {
  pending?: 'sending' | 'failed';
  /** Message vocal en cours d'envoi (pas encore arrivé au serveur). */
  voiceDraft?: { durationMs: number; waveform: number[]; /** Enregistrement local (réécoute pendant l'envoi). */ url: string };
  /** Code d'erreur du dernier essai (échec). */
  error?: string | undefined;
}

interface Thread {
  messages: ThreadMessage[];
  olderCursor: string | null;
  /**
   * Fil détaché des derniers messages (on est remonté jusqu'à un message cité) :
   * curseur de la page suivante. Null = on voit la fin du fil.
   */
  newerCursor: string | null;
  loading: boolean;
  loadingOlder: boolean;
  loadingNewer: boolean;
  /** Dernière relecture complète (rattrapage des modifications). */
  syncedAt: string;
}

/** Fichier choisi pour le prochain message : envoyé tout de suite, joint à l'envoi du message. */
export interface DraftFile {
  localId: string;
  name: string;
  size: number;
  /** Type annoncé par l'appareil (aperçu local), pas celui retenu par le serveur. */
  type: string;
  progress: number;
  status: 'uploading' | 'ready' | 'failed';
  error?: string | undefined;
  attachment?: AttachmentDTO;
  /** Aperçu local d'une image (URL objet). */
  localUrl?: string;
}

interface MessagingState {
  status: SocketStatus;
  loaded: boolean;
  conversations: ConversationDTO[];
  threads: Record<string, Thread>;
  /** conversation → personne → fin de l'indication « écrit… » (ms). */
  typing: Record<string, Record<string, number>>;
  /** Conversation affichée à l'écran (lue au fil de l'eau). */
  visibleId: string | null;
  /** Fichiers du message en préparation, par conversation. */
  drafts: Record<string, DraftFile[]>;
  /** Message auquel on répond (bandeau au-dessus de la saisie), par conversation. */
  replyTo: Record<string, MessageDTO>;
}

export const useMessagingStore = create<MessagingState>(() => ({
  status: 'idle', loaded: false, conversations: [], threads: {}, typing: {}, visibleId: null, drafts: {}, replyTo: {},
}));

const set = useMessagingStore.setState;
const get = useMessagingStore.getState;

const auth: MessagingAuth = {
  apiBase: API_BASE,
  token: () => useGameStore.getState().jwt,
  tenant: () => sessionInfo().tenantId,
};

export const messagingApi = new MessagingApi(auth);

const socket = new MessagingSocket(auth, {
  onEvent: (event) => handleEvent(event),
  onReady: () => { void resync(); },
  onStatus: (status) => set({ status }),
});

let started = false;

/** Démarre la connexion (entrée dans le campus). */
export function startMessaging(): void {
  if (started) return;
  started = true;
  socket.start();
}

/** Arrête et oublie tout (sortie du campus, déconnexion). */
export function stopMessaging(): void {
  if (!started) return;
  started = false;
  socket.stop();
  for (const files of Object.values(get().drafts)) for (const f of files) forgetDraft(f);
  set({ status: 'idle', loaded: false, conversations: [], threads: {}, typing: {}, visibleId: null, drafts: {}, replyTo: {} });
}

export function useUnreadTotal(): number {
  return useMessagingStore((s) => s.conversations.reduce((n, c) => n + c.unread, 0));
}

export function useDirectUnread(): number {
  return useMessagingStore((s) => s.conversations.reduce((n, c) => n + (c.kind === 'direct' || c.kind === 'group' ? c.unread : 0), 0));
}

// ── Lecture ──

export async function refreshConversations(): Promise<ConversationDTO[]> {
  try {
    const fromServer = await messagingApi.conversations();
    // Une conversation privée encore vide n'est pas renvoyée par le serveur : on
    // garde celle qu'on vient d'ouvrir (fiche d'une personne, nouveau message).
    const ids = new Set(fromServer.map((c) => c.id));
    const conversations = [
      ...fromServer,
      ...get().conversations.filter((c) => c.kind === 'direct' && !c.lastMessage && !ids.has(c.id)),
    ];
    set({ conversations, loaded: true });
    return conversations;
  } catch (err) {
    console.warn('[messaging] conversations indisponibles:', err);
    set({ loaded: true });
    return get().conversations;
  }
}

function sortMessages(list: ThreadMessage[]): ThreadMessage[] {
  return list.sort((a, b) => {
    if (!!a.pending !== !!b.pending) return a.pending ? 1 : -1;
    return a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
  });
}

/** Fusionne des messages du serveur dans un fil (remplace un envoi en cours de même clientId). */
function mergeInto(list: ThreadMessage[], incoming: MessageDTO[]): ThreadMessage[] {
  const byId = new Map(list.map((m) => [m.id, m]));
  const me = sessionInfo().userId;
  for (const m of incoming) {
    if (m.clientId && m.senderId === me) {
      for (const [id, p] of byId) if (p.pending && p.clientId === m.clientId) byId.delete(id);
    }
    byId.set(m.id, m);
  }
  return sortMessages([...byId.values()]);
}

function patchThread(conversationId: string, patch: (t: Thread) => Partial<Thread>) {
  set((s) => {
    const t = s.threads[conversationId];
    if (!t) return s;
    return { threads: { ...s.threads, [conversationId]: { ...t, ...patch(t) } } };
  });
}

function patchConversation(conversationId: string, patch: (c: ConversationDTO) => Partial<ConversationDTO>) {
  set((s) => ({ conversations: s.conversations.map((c) => (c.id === conversationId ? { ...c, ...patch(c) } : c)) }));
}

const emptyThread = (): Thread => ({
  messages: [], olderCursor: null, newerCursor: null, loading: true, loadingOlder: false, loadingNewer: false, syncedAt: '',
});

/** Charge (ou recharge) les derniers messages d'un fil. Un fil détaché revient à la fin. */
export async function openThread(conversationId: string): Promise<void> {
  const existing = get().threads[conversationId];
  if (!existing) {
    set((s) => ({ threads: { ...s.threads, [conversationId]: emptyThread() } }));
  }
  const syncedAt = new Date().toISOString();
  try {
    const page = await messagingApi.messages(conversationId);
    const since = existing?.syncedAt;
    const changes = since ? await messagingApi.changes(conversationId, since).catch(() => []) : [];
    patchThread(conversationId, (t) => {
      // Détaché : les messages chargés autour d'une citation ne touchent pas la fin
      // du fil (il y aurait un trou) ; on repart des derniers, envois en cours gardés.
      const detached = t.newerCursor !== null;
      const base = detached ? t.messages.filter((m) => m.pending) : t.messages;
      return {
        messages: mergeInto(base, [...page.messages, ...(detached ? [] : changes)]),
        olderCursor: !detached && existing && t.olderCursor !== null && t.messages.length > page.messages.length ? t.olderCursor : page.olderCursor,
        newerCursor: null,
        loading: false,
        syncedAt,
      };
    });
  } catch (err) {
    console.warn('[messaging] fil indisponible:', err);
    patchThread(conversationId, () => ({ loading: false }));
  }
}

export async function loadOlder(conversationId: string): Promise<void> {
  const t = get().threads[conversationId];
  if (!t || !t.olderCursor || t.loadingOlder) return;
  patchThread(conversationId, () => ({ loadingOlder: true }));
  try {
    const page = await messagingApi.messages(conversationId, { before: t.olderCursor });
    patchThread(conversationId, (cur) => ({ messages: mergeInto(cur.messages, page.messages), olderCursor: page.olderCursor, loadingOlder: false }));
  } catch {
    patchThread(conversationId, () => ({ loadingOlder: false }));
  }
}

/** Fil détaché : charge la page suivante (en descendant vers les derniers messages). */
export async function loadNewer(conversationId: string): Promise<void> {
  const t = get().threads[conversationId];
  if (!t || !t.newerCursor || t.loadingNewer) return;
  patchThread(conversationId, () => ({ loadingNewer: true }));
  try {
    const page = await messagingApi.messages(conversationId, { after: t.newerCursor });
    patchThread(conversationId, (cur) => ({ messages: mergeInto(cur.messages, page.messages), newerCursor: page.newerCursor, loadingNewer: false }));
  } catch {
    patchThread(conversationId, () => ({ loadingNewer: false }));
  }
}

/**
 * Amène un message dans le fil chargé (clic sur une citation) : déjà là, rien à
 * faire ; sinon le fil est rechargé autour de lui (détaché des derniers
 * messages). Faux s'il n'est plus lisible.
 */
export async function jumpToMessage(conversationId: string, messageId: string): Promise<boolean> {
  if (get().threads[conversationId]?.messages.some((m) => m.id === messageId)) return true;
  try {
    const page = await messagingApi.messages(conversationId, { around: messageId });
    patchThread(conversationId, (t) => ({
      messages: mergeInto(t.messages.filter((m) => m.pending), page.messages),
      olderCursor: page.olderCursor,
      newerCursor: page.newerCursor,
      loading: false,
    }));
    return page.messages.some((m) => m.id === messageId);
  } catch {
    return false;
  }
}

// ── Répondre ──

/** Aperçu d'un message cité, comme le renvoie le serveur. */
export function replyPreviewOf(m: MessageDTO): ReplyPreviewDTO {
  const files = m.deletedAt ? [] : m.attachments;
  const first = files[0];
  return {
    id: m.id, senderId: m.senderId, senderName: m.senderName,
    text: m.deletedAt ? '' : m.text.slice(0, REPLY_PREVIEW_LENGTH),
    deleted: !!m.deletedAt,
    attachment: first ? { kind: first.kind, durationMs: first.durationMs, count: files.length } : null,
  };
}

/** Répondre à ce message (null : annuler). */
export function setReplyTarget(conversationId: string, message: MessageDTO | null): void {
  set((s) => {
    const { [conversationId]: _old, ...rest } = s.replyTo;
    return { replyTo: message ? { ...rest, [conversationId]: message } : rest };
  });
}

/** Le message à citer pour le prochain envoi (retiré du bandeau). */
function takeReplyTarget(conversationId: string): ReplyPreviewDTO | null {
  const target = get().replyTo[conversationId];
  if (!target) return null;
  setReplyTarget(conversationId, null);
  return replyPreviewOf(target);
}

/** Fil affiché à l'écran (ou plus rien) : ses messages sont lus au fil de l'eau. */
export function setVisibleConversation(conversationId: string | null): void {
  set({ visibleId: conversationId });
  if (conversationId) void markRead(conversationId);
}

export async function markRead(conversationId: string): Promise<void> {
  const conv = get().conversations.find((c) => c.id === conversationId);
  if (conv && conv.unread === 0) return;
  patchConversation(conversationId, () => ({ unread: 0 }));
  await messagingApi.read(conversationId).catch(() => { /* rattrapé à la prochaine liste */ });
}

/** Ouvre (ou retrouve) la conversation privée avec cette personne. */
export async function openDirect(userId: string): Promise<string> {
  const { id } = await messagingApi.openDirect(userId);
  if (!get().conversations.some((c) => c.id === id)) {
    set((s) => ({
      conversations: [...s.conversations, {
        id, kind: 'direct', title: null, home: false, memberIds: [sessionInfo().userId, userId], adminIds: [],
        lastMessage: null, unread: 0, otherLastReadAt: null, otherDeliveredAt: null,
      }],
    }));
  }
  return id;
}

// ── Groupes ──

function applyGroup(info: GroupInfo) {
  patchConversation(info.id, () => ({ title: info.title, memberIds: info.memberIds, adminIds: info.adminIds }));
}

/** Retire une conversation de la liste et oublie son fil (départ, retrait d'un groupe). */
function forgetConversation(conversationId: string) {
  const drafts = get().drafts[conversationId] ?? [];
  for (const d of drafts) forgetDraft(d);
  set((s) => {
    const { [conversationId]: _t, ...threads } = s.threads;
    const { [conversationId]: _d, ...draftsLeft } = s.drafts;
    const { [conversationId]: _r, ...replyTo } = s.replyTo;
    return { conversations: s.conversations.filter((c) => c.id !== conversationId), threads, drafts: draftsLeft, replyTo };
  });
}

/** Crée un groupe et le renvoie (il apparaît tout de suite dans la liste). */
export async function createGroup(title: string, memberIds: string[]): Promise<string> {
  const info = await messagingApi.createGroup(title, memberIds);
  if (!get().conversations.some((c) => c.id === info.id)) {
    set((s) => ({
      conversations: [{
        id: info.id, kind: 'group', title: info.title, home: false, memberIds: info.memberIds, adminIds: info.adminIds,
        lastMessage: null, unread: 0, otherLastReadAt: null, otherDeliveredAt: null,
      }, ...s.conversations],
    }));
  }
  void refreshConversations();
  return info.id;
}

export async function renameGroup(id: string, title: string): Promise<void> {
  applyGroup(await messagingApi.renameGroup(id, title));
}

export async function addGroupMembers(id: string, userIds: string[]): Promise<void> {
  applyGroup(await messagingApi.addMembers(id, userIds));
}

export async function setGroupAdmin(id: string, userId: string, admin: boolean): Promise<void> {
  applyGroup(await messagingApi.setAdmin(id, userId, admin));
}

export async function removeGroupMember(id: string, userId: string): Promise<void> {
  applyGroup(await messagingApi.removeMember(id, userId));
}

/** Groupes qu'on quitte ou supprime soi-même : l'événement arrive par la socket avant la réponse de l'API. */
const leaving = new Set<string>();

async function goAway(id: string, call: () => Promise<unknown>): Promise<void> {
  leaving.add(id);
  try {
    await call();
    forgetConversation(id);
  } finally {
    leaving.delete(id);
  }
}

/** Quitter : la conversation disparaît de la liste. */
export function leaveGroup(id: string): Promise<void> {
  return goAway(id, () => messagingApi.leaveGroup(id));
}

/** Supprimer le groupe pour tout le monde (admins du groupe). */
export function deleteGroup(id: string): Promise<void> {
  return goAway(id, () => messagingApi.deleteGroup(id));
}

// ── Écriture ──

function newClientId(): string {
  return typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Envoie un message : le texte et les fichiers prêts de la conversation (les
 * fichiers quittent le brouillon tout de suite ; en cas d'échec, ils restent
 * sur le message à réessayer).
 */
export async function sendMessage(conversationId: string, text: string, retryOf?: ThreadMessage): Promise<void> {
  const { userId, isGuest } = sessionInfo();
  const displayName = useGameStore.getState().displayName ?? '';
  const clientId = retryOf?.clientId ?? newClientId();
  let attachments: AttachmentDTO[] = retryOf?.attachments ?? [];
  if (!retryOf) {
    const drafts = get().drafts[conversationId] ?? [];
    attachments = drafts.filter((d) => d.status === 'ready' && d.attachment).map((d) => d.attachment!);
    for (const d of drafts) forgetDraft(d);
    set((s) => ({ drafts: { ...s.drafts, [conversationId]: drafts.filter((d) => d.status === 'uploading') } }));
  }
  const local: ThreadMessage = retryOf
    // Message cité supprimé entre-temps : on renvoie sans la citation.
    ? { ...retryOf, pending: 'sending', ...(retryOf.error === 'REPLY_NOT_FOUND' ? { replyTo: null } : {}) }
    : {
      id: `local:${clientId}`, conversationId, senderId: userId, senderName: displayName, text,
      createdAt: new Date().toISOString(), editedAt: null, deletedAt: null, clientId, attachments, reactions: [],
      replyTo: takeReplyTarget(conversationId), system: null, pending: 'sending',
    };
  // Envoyer depuis un fil détaché ramène aux derniers messages.
  if (get().threads[conversationId]?.newerCursor) await openThread(conversationId);
  patchThread(conversationId, (t) => ({ messages: sortMessages([...t.messages.filter((m) => m.id !== local.id), local]) }));
  try {
    const message = await messagingApi.send(conversationId, {
      text: local.text, clientId,
      ...(local.replyTo ? { replyToId: local.replyTo.id } : {}),
      ...(attachments.length > 0 ? { attachmentIds: attachments.map((a) => a.id) } : {}),
      ...(isGuest ? { senderName: displayName } : {}),
    });
    patchThread(conversationId, (t) => ({ messages: mergeInto(t.messages, [message]) }));
    patchConversation(conversationId, (c) => (newer(message, c.lastMessage) ? { lastMessage: message, unread: 0 } : {}));
  } catch (err) {
    const code = err instanceof MessagingApiError ? err.code : 'NETWORK';
    patchThread(conversationId, (t) => ({
      messages: t.messages.map((m) => (m.id === local.id ? { ...m, pending: 'failed', error: code } : m)),
    }));
  }
}

/** Retire un envoi en échec (sans l'envoyer). */
export function discardFailed(conversationId: string, localId: string): void {
  voiceBlobs.delete(localId);
  const draft = get().threads[conversationId]?.messages.find((m) => m.id === localId)?.voiceDraft;
  if (draft) URL.revokeObjectURL(draft.url);
  patchThread(conversationId, (t) => ({ messages: t.messages.filter((m) => m.id !== localId) }));
}

/** Enregistrement d'un vocal pas encore arrivé au serveur (pour « Réessayer »). */
const voiceBlobs = new Map<string, Blob>();

/**
 * Message vocal (tasks/messagerie-plan.md, § 6) : affiché tout de suite dans le
 * fil, envoyé au serveur (converti en M4A), puis publié comme un message dont
 * c'est le seul fichier. En échec, « Réessayer » reprend où ça s'est arrêté.
 */
export async function sendVoice(conversationId: string, blob: Blob, durationMs: number, waveform: number[], retryOf?: ThreadMessage): Promise<void> {
  if (retryOf?.attachments.length) { await sendMessage(conversationId, '', retryOf); return; }
  const { userId } = sessionInfo();
  const clientId = retryOf?.clientId ?? newClientId();
  const local: ThreadMessage = retryOf
    ? { ...retryOf, pending: 'sending', error: undefined }
    : {
      id: `local:${clientId}`, conversationId, senderId: userId, senderName: useGameStore.getState().displayName ?? '', text: '',
      createdAt: new Date().toISOString(), editedAt: null, deletedAt: null, clientId, attachments: [], reactions: [],
      replyTo: takeReplyTarget(conversationId), system: null, pending: 'sending',
      voiceDraft: { durationMs, waveform, url: URL.createObjectURL(blob) },
    };
  voiceBlobs.set(local.id, blob);
  patchThread(conversationId, (t) => ({ messages: sortMessages([...t.messages.filter((m) => m.id !== local.id), local]) }));
  let attachment: AttachmentDTO;
  try {
    const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
    attachment = await messagingApi.upload(conversationId, blob, `vocal.${ext}`, { voice: true });
  } catch (err) {
    const code = err instanceof MessagingApiError ? err.code : 'NETWORK';
    patchThread(conversationId, (t) => ({
      messages: t.messages.map((m) => (m.id === local.id ? { ...m, pending: 'failed', error: code } : m)),
    }));
    return;
  }
  voiceBlobs.delete(local.id);
  await sendMessage(conversationId, '', { ...local, attachments: [attachment] });
  const sent = get().threads[conversationId]?.messages.some((m) => m.id === local.id);
  if (!sent && local.voiceDraft) URL.revokeObjectURL(local.voiceDraft.url);
}

/**
 * GIF choisi dans la recherche : le serveur le range comme un fichier joint
 * (téléchargé chez Giphy), puis il part comme un message dont c'est le seul
 * fichier, avec la citation en cours s'il y en a une. Rejette en cas d'échec
 * (la fenêtre de recherche le dit et reste ouverte).
 */
export async function sendGif(conversationId: string, gifId: string): Promise<void> {
  const attachment = await messagingApi.attachGif(conversationId, gifId);
  const { userId } = sessionInfo();
  const clientId = newClientId();
  await sendMessage(conversationId, '', {
    id: `local:${clientId}`, conversationId, senderId: userId, senderName: useGameStore.getState().displayName ?? '', text: '',
    createdAt: new Date().toISOString(), editedAt: null, deletedAt: null, clientId, attachments: [attachment], reactions: [],
    replyTo: takeReplyTarget(conversationId), system: null, pending: 'sending',
  });
}

/** « Réessayer » d'un vocal : renvoie l'enregistrement, ou seulement le message si le fichier est déjà arrivé. */
export function retryVoice(message: ThreadMessage): void {
  const blob = voiceBlobs.get(message.id);
  if (message.attachments.length > 0 || !blob) { void sendMessage(message.conversationId, '', message); return; }
  void sendVoice(message.conversationId, blob, message.voiceDraft?.durationMs ?? 0, message.voiceDraft?.waveform ?? [], message);
}

export async function editMessage(message: MessageDTO, text: string): Promise<void> {
  const updated = await messagingApi.edit(message.id, text);
  applyUpdate(updated);
}

export async function deleteMessage(message: MessageDTO): Promise<void> {
  const updated = await messagingApi.remove(message.id);
  applyUpdate(updated);
}

// ── Réactions ──

/** Dernier changement demandé par message : une réponse plus ancienne n'écrase pas un clic plus récent. */
const reactionGeneration = new Map<string, number>();

function setReactions(conversationId: string, messageId: string, reactions: MessageReactionDTO[]) {
  patchThread(conversationId, (t) => ({
    messages: t.messages.some((m) => m.id === messageId) ? t.messages.map((m) => (m.id === messageId ? { ...m, reactions } : m)) : t.messages,
  }));
  patchConversation(conversationId, (c) => (c.lastMessage?.id === messageId ? { lastMessage: { ...c.lastMessage, reactions } } : {}));
}

/**
 * Ajoute sa réaction, ou la retire si elle y est déjà (`force` impose le sens :
 * le double-clic ne fait qu'ajouter). Affiché tout de suite, corrigé par la
 * réponse du serveur, annulé en cas d'échec.
 */
export async function toggleReaction(message: MessageDTO, reactionId: string, force?: boolean): Promise<void> {
  const me = sessionInfo().userId;
  if (!me || message.deletedAt) return;
  const current = get().threads[message.conversationId]?.messages.find((m) => m.id === message.id) ?? message;
  const before = current.reactions;
  const mine = before.some((r) => r.reactionId === reactionId && r.userIds.includes(me));
  const on = force ?? !mine;
  if (on === mine) return;
  if (on) recordReactionUse(reactionId);
  const next = on
    ? (before.some((r) => r.reactionId === reactionId)
      ? before.map((r) => (r.reactionId === reactionId ? { ...r, userIds: [...r.userIds, me] } : r))
      : [...before, { reactionId, userIds: [me] }])
    : before.map((r) => (r.reactionId === reactionId ? { ...r, userIds: r.userIds.filter((u) => u !== me) } : r)).filter((r) => r.userIds.length > 0);
  const generation = (reactionGeneration.get(message.id) ?? 0) + 1;
  reactionGeneration.set(message.id, generation);
  setReactions(message.conversationId, message.id, next);
  try {
    const res = await messagingApi.react(message.id, reactionId, on);
    if (reactionGeneration.get(message.id) === generation) setReactions(message.conversationId, message.id, res.reactions);
  } catch (err) {
    if (reactionGeneration.get(message.id) === generation) setReactions(message.conversationId, message.id, before);
    const code = err instanceof MessagingApiError ? err.code : 'NETWORK';
    EventBus.emit('ui:toast', {
      message: code === 'TOO_MANY_REACTIONS' ? 'Tu as déjà mis beaucoup de réactions à ce message.'
        : code === 'RATE_LIMITED' ? 'Doucement : trop de réactions d’un coup, réessaie dans un instant.'
        : 'Réaction non enregistrée, réessaie.',
      tone: 'warning',
    });
  }
}

export function notifyTyping(conversationId: string): void {
  socket.typing(conversationId);
}

// ── Fichiers ──

const uploads = new Map<string, AbortController>();

function forgetDraft(d: DraftFile) {
  uploads.get(d.localId)?.abort();
  uploads.delete(d.localId);
  if (d.localUrl) URL.revokeObjectURL(d.localUrl);
}

function patchDraft(conversationId: string, localId: string, patch: Partial<DraftFile>) {
  set((s) => ({
    drafts: { ...s.drafts, [conversationId]: (s.drafts[conversationId] ?? []).map((d) => (d.localId === localId ? { ...d, ...patch } : d)) },
  }));
}

async function uploadDraft(conversationId: string, draft: DraftFile, file: File) {
  const controller = new AbortController();
  uploads.set(draft.localId, controller);
  try {
    const attachment = await messagingApi.upload(conversationId, file, file.name, {
      signal: controller.signal,
      onProgress: (ratio) => patchDraft(conversationId, draft.localId, { progress: ratio }),
    });
    patchDraft(conversationId, draft.localId, { status: 'ready', progress: 1, attachment });
  } catch (err) {
    const code = err instanceof MessagingApiError ? err.code : 'NETWORK';
    if (code !== 'ABORTED') patchDraft(conversationId, draft.localId, { status: 'failed', error: code });
  } finally {
    uploads.delete(draft.localId);
  }
}

const retryFiles = new Map<string, File>();

/** Ajoute des fichiers au message en préparation (envoyés aussitôt). */
export function addDraftFiles(conversationId: string, files: File[]): void {
  const existing = get().drafts[conversationId] ?? [];
  const room = ATTACHMENTS_PER_MESSAGE - existing.length;
  if (files.length > room) {
    EventBus.emit('ui:toast', { message: `${ATTACHMENTS_PER_MESSAGE} fichiers au plus par message.`, tone: 'warning' });
  }
  const added: Array<{ draft: DraftFile; file: File }> = files.slice(0, Math.max(room, 0)).map((file) => ({
    file,
    draft: {
      localId: newClientId(), name: file.name, size: file.size, type: file.type, progress: 0,
      status: file.size > ATTACHMENT_UPLOAD_MAX_BYTES ? 'failed' : 'uploading',
      ...(file.size > ATTACHMENT_UPLOAD_MAX_BYTES ? { error: 'FILE_TOO_LARGE' } : {}),
      ...(file.type.startsWith('image/') ? { localUrl: URL.createObjectURL(file) } : {}),
    },
  }));
  if (added.length === 0) return;
  set((s) => ({ drafts: { ...s.drafts, [conversationId]: [...(s.drafts[conversationId] ?? []), ...added.map((a) => a.draft)] } }));
  for (const { draft, file } of added) {
    retryFiles.set(draft.localId, file);
    if (draft.status === 'uploading') void uploadDraft(conversationId, draft, file);
  }
}

export function removeDraftFile(conversationId: string, localId: string): void {
  const draft = (get().drafts[conversationId] ?? []).find((d) => d.localId === localId);
  if (draft) forgetDraft(draft);
  retryFiles.delete(localId);
  set((s) => ({ drafts: { ...s.drafts, [conversationId]: (s.drafts[conversationId] ?? []).filter((d) => d.localId !== localId) } }));
}

export function retryDraftFile(conversationId: string, localId: string): void {
  const draft = (get().drafts[conversationId] ?? []).find((d) => d.localId === localId);
  const file = retryFiles.get(localId);
  if (!draft || !file || draft.error === 'FILE_TOO_LARGE') return;
  patchDraft(conversationId, localId, { status: 'uploading', progress: 0, error: undefined });
  void uploadDraft(conversationId, draft, file);
}

// Aperçus et fichiers chargés en blob (le jeton passe en en-tête) : une URL objet par fichier.
const blobUrls = new Map<string, Promise<string | null>>();

export function attachmentBlobUrl(attachmentId: string, variant: 'original' | 'preview'): Promise<string | null> {
  const key = `${variant}:${attachmentId}`;
  let p = blobUrls.get(key);
  if (!p) {
    p = messagingApi.blob(attachmentId, variant).then((b) => URL.createObjectURL(b)).catch(() => {
      blobUrls.delete(key);
      return null;
    });
    blobUrls.set(key, p);
  }
  return p;
}

/** Adresse signée (vidéo, son, téléchargement), gardée jusqu'à 5 min avant son expiration. */
const signedUrls = new Map<string, { url: string; expiresAt: number }>();

export async function attachmentFileUrl(attachmentId: string, download = false): Promise<string> {
  const key = download ? `${attachmentId}|download` : attachmentId;
  const cached = signedUrls.get(key);
  if (cached && cached.expiresAt - Date.now() > 5 * 60_000) return cached.url;
  const { url, expiresAt } = await messagingApi.fileUrl(attachmentId, download);
  signedUrls.set(key, { url, expiresAt: new Date(expiresAt).getTime() });
  return url;
}

// ── Temps réel ──

function newer(m: MessageDTO, than: MessageDTO | null): boolean {
  return !than || m.createdAt > than.createdAt || (m.createdAt === than.createdAt && m.id > than.id);
}

function applyUpdate(message: MessageDTO) {
  // Le message lui-même, et les citations qui le reprennent (modifié, supprimé).
  const quote = replyPreviewOf(message);
  patchThread(message.conversationId, (t) => ({
    messages: t.messages.map((m) => (m.id === message.id ? message
      : m.replyTo?.id === message.id ? { ...m, replyTo: quote } : m)),
  }));
  patchConversation(message.conversationId, (c) => (c.lastMessage?.id === message.id ? { lastMessage: message } : {}));
}

function handleEvent(event: MessagingEvent) {
  const me = sessionInfo().userId;
  switch (event.type) {
    case 'message.created': {
      const m = event.message;
      const conv = get().conversations.find((c) => c.id === m.conversationId);
      // Fil détaché (remonté à une citation) : le message arrivera en redescendant.
      patchThread(m.conversationId, (t) => (t.newerCursor ? {} : { messages: mergeInto(t.messages, [m]) }));
      // La personne a fini d'écrire.
      set((s) => {
        const typing = s.typing[m.conversationId];
        if (!typing?.[m.senderId]) return s;
        const { [m.senderId]: _gone, ...rest } = typing;
        return { typing: { ...s.typing, [m.conversationId]: rest } };
      });
      if (!conv) { void refreshConversations(); }
      const mine = m.senderId === me;
      // Message système d'un groupe : jamais compté comme non lu (comme le serveur), jamais notifié.
      const quiet = mine || !!m.system;
      const seen = quiet || (get().visibleId === m.conversationId && document.visibilityState === 'visible');
      if (conv) {
        patchConversation(m.conversationId, (c) => ({
          lastMessage: newer(m, c.lastMessage) ? m : c.lastMessage,
          unread: m.system ? c.unread : seen ? 0 : c.unread + 1,
        }));
      }
      if (!mine && seen && !m.system) void messagingApi.read(m.conversationId, m.id).catch(() => {});
      if (!quiet) {
        EventBus.emit('messaging:message-received', {
          conversationId: m.conversationId, kind: conv?.kind ?? 'direct', senderId: m.senderId,
          senderName: m.senderName, text: previewText(m), seen,
          mentionsMe: mentionsName(m.text, useMembersStore.getState().members.get(me)?.displayName ?? useGameStore.getState().displayName ?? ''),
        });
      }
      break;
    }
    case 'message.updated':
      applyUpdate(event.message);
      break;
    case 'reactions.updated':
      setReactions(event.conversationId, event.messageId, event.reactions);
      break;
    case 'read.updated':
      if (event.userId !== me) {
        patchConversation(event.conversationId, (c) => ({
          otherLastReadAt: !c.otherLastReadAt || event.lastReadAt > c.otherLastReadAt ? event.lastReadAt : c.otherLastReadAt,
        }));
      }
      break;
    case 'delivered.updated':
      if (event.userId !== me) {
        patchConversation(event.conversationId, (c) => ({
          otherDeliveredAt: !c.otherDeliveredAt || event.deliveredAt > c.otherDeliveredAt ? event.deliveredAt : c.otherDeliveredAt,
        }));
      }
      break;
    case 'typing':
      set((s) => ({
        typing: { ...s.typing, [event.conversationId]: { ...s.typing[event.conversationId], [event.userId]: Date.now() + TYPING_TTL_MS } },
      }));
      break;
    case 'conversation.added':
      void refreshConversations();
      break;
    case 'conversation.updated':
      applyGroup(event.conversation);
      break;
    case 'conversation.removed': {
      // Pas déjà retirée par leaveGroup() : quelqu'un d'autre nous a fait sortir.
      const conv = get().conversations.find((c) => c.id === event.conversationId);
      if (!conv) break;
      forgetConversation(event.conversationId);
      if (conv.kind === 'group' && !leaving.has(event.conversationId)) {
        EventBus.emit('ui:toast', {
          message: event.reason === 'deleted'
            ? `Le groupe « ${conv.title ?? ''} » a été supprimé.`
            : `Tu ne fais plus partie du groupe « ${conv.title ?? ''} ».`,
          tone: 'info',
        });
      }
      break;
    }
  }
}

/** Après chaque (re)connexion : liste et fils ouverts relus. */
async function resync() {
  await refreshConversations();
  for (const id of Object.keys(get().threads)) void openThread(id);
  const visible = get().visibleId;
  if (visible && document.visibilityState === 'visible') void markRead(visible);
}
